import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { useContinuousData } from '../../../../hooks/useContinuousData';
import { useUser } from '../../../../hooks/useUser';
import { getContinuousHeartRateRange as getContinuousRange } from './continuousHeartRate';
import {
    CONTINUOUS_SLEEP_TYPE,
    normalizeContinuousSleep,
    summarizeSleep,
    buildSleepChartData,
    formatSleepDuration
} from './continuousSleep';
import { getDailyRequestRange } from './continuousDailyRange';

const KEY = 'userPage.overview.rehabilitation.sleepChart';

const SummaryItem = ({ label, value, divider }) => (
    <Box
        sx={{
            minWidth: 0,
            px: 1.5,
            textAlign: 'center',
            borderLeft: divider ? '1px solid #D9DEE2' : 0
        }}
    >
        <Typography
            variant="caption"
            sx={{ display: 'block', color: '#52616B', fontWeight: 700 }}
        >
            {label}
        </Typography>
        <Typography sx={{ color: '#263238', fontSize: '0.875rem', fontWeight: 700 }}>
            {value}
        </Typography>
    </Box>
);
SummaryItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    divider: PropTypes.bool
};

export const SleepTooltip = ({
    active,
    payload,
    dateFormatter,
    timeFormatter,
    aggregated,
    t
}) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    if (!point) return null;
    const date = (value) => dateFormatter.format(dayjs.utc(value).toDate());
    const fields =
        point.durationMinutes === null
            ? [{ label: t(`${KEY}.missing`), value: '' }]
            : [
                  {
                      label: t(`${KEY}.${aggregated ? 'averageDuration' : 'duration'}`),
                      value: formatSleepDuration(point.durationMinutes)
                  },
                  ...(aggregated
                      ? [
                            {
                                label: t(`${KEY}.recordedNights`),
                                value: String(point.recorded)
                            }
                        ]
                      : [
                            {
                                label: t(`${KEY}.periodStart`),
                                value: timeFormatter.format(new Date(point.sleepStart))
                            },
                            {
                                label: t(`${KEY}.periodEnd`),
                                value: timeFormatter.format(new Date(point.sleepEnd))
                            }
                        ]),
                  ...(point.goalMinutes === null
                      ? []
                      : [
                            {
                                label: t(`${KEY}.${aggregated ? 'averageGoal' : 'goal'}`),
                                value: formatSleepDuration(point.goalMinutes)
                            }
                        ])
              ];
    return (
        <Box
            sx={{
                maxWidth: 320,
                p: 1.25,
                bgcolor: '#FFFFFF',
                border: '1px solid #D7E0E6',
                borderRadius: 1,
                boxShadow: '0 4px 12px rgba(38, 50, 56, 0.12)'
            }}
        >
            <Typography variant="caption" sx={{ color: '#263238', fontWeight: 700 }}>
                {date(point.date)}
                {aggregated && point.endDate !== point.date
                    ? ` - ${date(point.endDate)}`
                    : ''}
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.5, mt: 0.5 }}>
                {fields.map((field) => (
                    <Box
                        key={field.label}
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {field.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {field.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
SleepTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.object),
    dateFormatter: PropTypes.shape({ format: PropTypes.func.isRequired }).isRequired,
    timeFormatter: PropTypes.shape({ format: PropTypes.func.isRequired }).isRequired,
    aggregated: PropTypes.bool.isRequired,
    t: PropTypes.func.isRequired
};

const SleepEvolutionChart = ({ patientUuid, period, patientCreated }) => {
    const { t, i18n } = useTranslation();
    const { user } = useUser();
    const zone = user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const range = useMemo(
        () => getContinuousRange({ period, patientCreated, zone }),
        [period, patientCreated, zone]
    );
    const query = useContinuousData({
        patientUuid,
        dtype: CONTINUOUS_SLEEP_TYPE,
        ...getDailyRequestRange(range, zone)
    });
    const days = useMemo(
        () => normalizeContinuousSleep(query.data, { zone, ...range }),
        [query.data, zone, range]
    );
    const data = useMemo(() => buildSleepChartData(days, period), [days, period]);
    const summary = useMemo(() => summarizeSleep(days), [days]);
    const aggregated = period === '90d' || period === 'all';
    const locale = i18n.resolvedLanguage?.startsWith('pt') ? 'pt-PT' : 'en-GB';
    const axisFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: period === 'all' ? undefined : 'numeric',
                month: 'short',
                year: period === 'all' ? '2-digit' : undefined,
                timeZone: 'UTC'
            }),
        [locale, period]
    );
    const dateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                timeZone: 'UTC'
            }),
        [locale]
    );
    const timeFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: zone
            }),
        [locale, zone]
    );

    if (query.isPending)
        return (
            <Box sx={{ display: 'grid', height: 300, gap: 1 }}>
                <Skeleton width="30%" height={20} />
                <Skeleton variant="rounded" height={260} />
            </Box>
        );
    if (query.isError)
        return (
            <Box
                sx={{
                    display: 'flex',
                    height: 300,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5
                }}
            >
                <Typography color="error" variant="body2">
                    {t(`${KEY}.loadError`)}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => query.refetch()}>
                    {t('userPage.overview.measurements.retry')}
                </Button>
            </Box>
        );
    if (!summary.recorded)
        return (
            <Box
                sx={{
                    display: 'flex',
                    height: 300,
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {t(`${KEY}.noData`)}
                </Typography>
            </Box>
        );

    return (
        <Box sx={{ display: 'flex', height: 300, flexDirection: 'column' }}>
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 0.5
                }}
            >
                {t(`${KEY}.subtitle`)}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    minHeight: 52,
                    alignItems: 'center',
                    mb: 0.5
                }}
            >
                <SummaryItem
                    label={t(`${KEY}.averageDuration`)}
                    value={formatSleepDuration(summary.averageMinutes)}
                />
                <SummaryItem
                    label={t(`${KEY}.recordedNights`)}
                    value={new Intl.NumberFormat(locale).format(summary.recorded)}
                    divider
                />
            </Box>
            <Box
                role="img"
                aria-label={t(`${KEY}.chartLabel`)}
                sx={{ flex: 1, minHeight: 0 }}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke="#E6EDF2"
                            strokeDasharray="3 3"
                        />
                        <XAxis
                            dataKey="date"
                            axisLine={{ stroke: '#CAD5DD' }}
                            tickLine={false}
                            minTickGap={24}
                            tick={{ fill: '#74838E', fontSize: 10 }}
                            tickFormatter={(value) =>
                                axisFormatter.format(dayjs.utc(value).toDate())
                            }
                        />
                        <YAxis
                            width={42}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#74838E', fontSize: 10 }}
                            tickFormatter={(value) =>
                                `${Math.round((value / 60) * 10) / 10}h`
                            }
                        />
                        <Tooltip
                            filterNull={false}
                            content={
                                <SleepTooltip
                                    dateFormatter={dateFormatter}
                                    timeFormatter={timeFormatter}
                                    aggregated={aggregated}
                                    t={t}
                                />
                            }
                        />
                        <Legend
                            verticalAlign="top"
                            align="right"
                            height={24}
                            iconSize={10}
                            wrapperStyle={{ color: '#5E6C76', fontSize: 10 }}
                        />
                        <Bar
                            dataKey="durationMinutes"
                            name={t(
                                `${KEY}.${aggregated ? 'averageDuration' : 'duration'}`
                            )}
                            fill="#0A6FB4"
                            maxBarSize={28}
                            isAnimationActive={false}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};
SleepEvolutionChart.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    period: PropTypes.oneOf(['7d', '30d', '90d', 'all']).isRequired,
    patientCreated: PropTypes.string
};
export default SleepEvolutionChart;
